import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, EyeOff, Pencil, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import {
  createSettingsCategory,
  getSettingsCategories,
  reorderSettingsCategories,
  updateSettingsCategory,
  type CategorySettingDto,
} from '@/lib/api/v2/settings'
import { moveItem } from './categories-order'
import { WinnerGroupMappings } from './WinnerGroupMappings'
import { WinnerCatalogSupplyMappings } from './WinnerCatalogSupplyMappings'

const LABEL_MAX_LENGTH = 256

interface CategoryDialogProps {
  open: boolean
  initial: CategorySettingDto | null
  isSaving: boolean
  onClose: () => void
  onSave: (label: string, translations: Record<string, string>, isHidden: boolean) => Promise<void>
}

function CategoryDialog({ open, initial, isSaving, onClose, onSave }: CategoryDialogProps) {
  const [label, setLabel] = useState(initial?.label ?? '')
  const [dutch, setDutch] = useState(initial?.translations.nl ?? '')
  const [isHidden, setIsHidden] = useState(initial?.isHidden ?? false)

  const handleSave = async () => {
    if (!label.trim()) {
      showErrorToast('Le nom de la catégorie est obligatoire.')
      return
    }
    // Other translations (English for the base categories) are kept as they are; an empty Dutch
    // field removes the Dutch one.
    const { nl: _previousDutch, ...others } = initial?.translations ?? {}
    const translations: Record<string, string> = dutch.trim() ? { ...others, nl: dutch.trim() } : others
    await onSave(label.trim(), translations, isHidden)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
          <DialogDescription>Le nom est le titre de section imprimé sur vos devis et factures.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="category-label">Nom</Label>
            <Input
              id="category-label"
              value={label}
              maxLength={LABEL_MAX_LENGTH}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex. Sanitaires"
              data-testid="settings-category-label-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="category-label-nl">Nom en néerlandais (facultatif)</Label>
            <Input
              id="category-label-nl"
              value={dutch}
              maxLength={LABEL_MAX_LENGTH}
              onChange={(e) => setDutch(e.target.value)}
              placeholder="Ex. Sanitair"
              data-testid="settings-category-label-nl-input"
            />
            <p className="text-xs text-muted-foreground">Sans traduction, un document en néerlandais imprime le nom.</p>
          </div>
          {initial && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="category-hidden"
                checked={isHidden}
                onCheckedChange={(v) => setIsHidden(Boolean(v))}
                data-testid="settings-category-hidden-checkbox"
              />
              <Label htmlFor="category-hidden" className="cursor-pointer font-normal">
                Masquer dans la liste de choix (les lignes qui l'utilisent la gardent)
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="settings-category-save-btn">
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CategoriesSettings() {
  const [categories, setCategories] = useState<CategorySettingDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CategorySettingDto | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      setCategories(await getSettingsCategories())
    } catch {
      setError('Impossible de charger les catégories.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (category: CategorySettingDto) => {
    setEditing(category)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditing(null)
  }

  const handleSave = async (label: string, translations: Record<string, string>, isHidden: boolean) => {
    setIsSaving(true)
    try {
      if (editing) {
        const updated = await updateSettingsCategory(editing.id, { label, translations, isHidden })
        setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
        showSuccessToast('Catégorie mise à jour.')
      } else {
        const created = await createSettingsCategory({ label, translations })
        setCategories((prev) => [...prev, created])
        showSuccessToast('Catégorie créée.')
      }
      closeDialog()
    } catch (e) {
      showErrorToast(e instanceof ApiError && e.message ? e.message : "Erreur lors de l'enregistrement.")
    } finally {
      setIsSaving(false)
    }
  }

  const move = async (index: number, direction: -1 | 1) => {
    const next = moveItem(categories, index, direction)
    if (next === categories) return
    setCategories(next)
    try {
      await reorderSettingsCategories(next.map((c) => c.id))
    } catch {
      showErrorToast('Erreur lors de la réorganisation.')
      load()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          L'ordre est celui de la liste de choix. Un renommage vaut pour les prochains documents : ceux déjà émis ne changent pas.
        </p>
        <Button size="sm" onClick={openCreate} data-testid="settings-category-add-btn">
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      <div className="rounded-lg border divide-y" data-testid="settings-category-list">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className="flex items-center gap-3 px-4 py-3"
            data-testid={`settings-category-row-${category.id}`}
          >
            <div className="flex flex-col gap-0.5 text-muted-foreground">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Monter ${category.label}`}
                data-testid={`settings-category-up-${category.id}`}
                className="hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronUp className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === categories.length - 1}
                aria-label={`Descendre ${category.label}`}
                data-testid={`settings-category-down-${category.id}`}
                className="hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronDown className="size-3.5" />
              </button>
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{category.label}</div>
              {category.translations.nl && (
                <div className="text-xs text-muted-foreground truncate">NL : {category.translations.nl}</div>
              )}
            </div>

            {category.isHidden && (
              <Badge variant="outline" className="text-xs gap-1">
                <EyeOff className="size-3" />
                Masquée
              </Badge>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => openEdit(category)}
              aria-label={`Modifier ${category.label}`}
              data-testid={`settings-category-edit-${category.id}`}
            >
              <Pencil className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <WinnerGroupMappings categories={categories} />
      <WinnerCatalogSupplyMappings />

      {/* key forces a remount so the dialog's initial state follows the category being edited */}
      <CategoryDialog
        key={editing?.id ?? `new-${dialogOpen}`}
        open={dialogOpen}
        initial={editing}
        isSaving={isSaving}
        onClose={closeDialog}
        onSave={handleSave}
      />
    </div>
  )
}
